import { type ReactNode, useCallback, useSyncExternalStore } from "react";
import { Box, Button, Chip, Divider, IconButton, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Close, HighlightAlt, Undo as UndoIcon, Redo as RedoIcon } from "@mui/icons-material";
import { PortalTooltip } from "../PortalTooltip";
import { useMapTool } from "../../map/context/MapToolContext";
import { useMapEdit } from "../../map/context/MapEditContext";
import type { UndoStack } from "../../../application/map/undoStack";
import type { ToolId } from "../../../application/map/tools/types";
import type { JobMapViewModel } from "./JobMap.vm";
import type { Job } from "../../../models";

// ── Tool group definitions (same as MapToolbar) ─────────────────────────────

interface ToolDef {
	id: ToolId;
	label: string;
	tooltip: string;
}

const TOOL_GROUPS: ToolDef[][] = [
	[
		{ id: "draw-boundary", label: "Boundary", tooltip: "Draw boundary" },
		{ id: "draw-zone", label: "Zone", tooltip: "Draw zone" },
		{ id: "delete", label: "Delete", tooltip: "Delete polygon or point" },
	],
	[
		// { id: "cut", label: "Cut", tooltip: "Cut polygon with line" },
		// { id: "erase", label: "Erase", tooltip: "Erase polygon section" },
		// { id: "merge-polys", label: "Merge", tooltip: "Merge two polygons" },
		// { id: "merge-holes", label: "Merge Holes", tooltip: "Merge two holes" },
	],
	// [{ id: "lasso", label: "Lasso", tooltip: "Lasso select" }],
	[
		{ id: "point-add", label: "+Pt", tooltip: "Add sample point" },
		{ id: "point-delete", label: "-Pt", tooltip: "Delete sample point" },
		{ id: "point-modify", label: "~Pt", tooltip: "Modify sample point" },
	],
];

// ── Props ───────────────────────────────────────────────────────────────────

export interface JobMapToolbarProps {
	vm: JobMapViewModel;
	editable?: boolean;
	toolbarActions?: ReactNode;
	onClose?: () => void;
	selectedJobId?: string | null;
	onClearSelectedJob?: () => void;
	jobs: Job[];
	onSave?: () => void;
	onCancel?: () => void;
}

// ── Component ───────────────────────────────────────────────────────────────

export function JobMapToolbar({
	vm,
	editable,
	toolbarActions,
	onClose,
	selectedJobId,
	onClearSelectedJob,
	jobs,
	onSave,
	onCancel,
}: JobMapToolbarProps) {
	const selectionText = getSelectionText(vm.effectiveSelectedFeatureIds);

	const selectedJobName = selectedJobId
		? (jobs.find((j) => j.id === selectedJobId)?.importFieldName ??
			jobs.find((j) => j.id === selectedJobId)?.fieldName ??
			"Unknown")
		: null;

	return (
		<Box
			className="map-toolbar"
			onDragStart={(e) => e.preventDefault()}
			sx={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				padding: 1.5,
				borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
				flexShrink: 0,
				flexWrap: "wrap",
				gap: 1,
				userSelect: "none",
			}}
		>
			{/* Left: job count + selection info + selected job chip */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
				<Typography variant="h6">
					{vm.effectiveJobs.length} {vm.effectiveJobs.length === 1 ? "Job" : "Jobs"}
				</Typography>
				{selectedJobName && (
					<Chip label={selectedJobName} size="small" onDelete={onClearSelectedJob} color="primary" variant="outlined" />
				)}
				{selectionText && (
					<Typography variant="body2" color="error.main">
						{selectionText}
					</Typography>
				)}
			</Box>

			{/* Right: tools + actions + close */}
			<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
				{editable && <EditToolGroups onSave={onSave} onCancel={onCancel} />}
				{toolbarActions}
				{onClose && (
					<IconButton onClick={onClose} aria-label="close map" size="small">
						<Close />
					</IconButton>
				)}
			</Box>
		</Box>
	);
}

// ── Undo state hook (subscribe to UndoStack changes) ─────────────────────────

function useUndoState(undoStack: UndoStack) {
	const subscribe = useCallback((cb: () => void) => undoStack.onChange(cb), [undoStack]);
	const canUndo = useSyncExternalStore(subscribe, () => undoStack.canUndo);
	const canRedo = useSyncExternalStore(subscribe, () => undoStack.canRedo);
	return { canUndo, canRedo };
}

// ── Edit tool groups (uses MapToolContext) ───────────────────────────────────

function EditToolGroups({ onSave, onCancel }: { onSave?: () => void; onCancel?: () => void }) {
	const { state, toggle, deactivate, getRegistered } = useMapTool();
	const { undoStack } = useMapEdit();
	const { canUndo, canRedo } = useUndoState(undoStack);
	const registeredTool = state.activeTool ? getRegistered(state.activeTool) : null;
	const activeToolLabel =
		registeredTool?.label ?? TOOL_GROUPS.flat().find((t) => t.id === state.activeTool)?.label ?? state.activeTool;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
			{/* Row 1: undo/redo + active tool chip */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<PortalTooltip disableInteractive placement="top" title="Undo (Ctrl+Z)">
					<span>
						<IconButton size="small" onClick={() => undoStack.undo()} disabled={!canUndo} aria-label="Undo">
							<UndoIcon fontSize="small" />
						</IconButton>
					</span>
				</PortalTooltip>
				<PortalTooltip disableInteractive placement="top" title="Redo (Ctrl+Shift+Z)">
					<span>
						<IconButton size="small" onClick={() => undoStack.redo()} disabled={!canRedo} aria-label="Redo">
							<RedoIcon fontSize="small" />
						</IconButton>
					</span>
				</PortalTooltip>
				{state.activeTool && (
					<>
						<Divider orientation="vertical" flexItem />
						<Chip label={activeToolLabel} size="small" color="primary" onDelete={deactivate} />
					</>
				)}
				{state.activeTool === "edit-vertices" && onSave && onCancel && (
					<>
						<Divider orientation="vertical" flexItem />
						<Button variant="outlined" color="inherit" size="small" onClick={onCancel}>
							Cancel
						</Button>
						<Button variant="contained" color="success" size="small" onClick={onSave}>
							Save
						</Button>
					</>
				)}
			</Box>
			{/* Row 2: tool groups */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				{TOOL_GROUPS.map((group, gi) => (
					<ToggleButtonGroup
						key={gi}
						size="small"
						exclusive
						value={state.activeTool}
						onChange={(_, value) => {
							if (value) toggle(value as ToolId);
							else if (state.activeTool) toggle(state.activeTool);
						}}
					>
						{group.map((tool) => (
							<PortalTooltip key={tool.id} disableInteractive placement="top" title={tool.tooltip}>
								<ToggleButton value={tool.id} sx={{ textTransform: "none", px: 1.5 }}>
									{tool.label}
								</ToggleButton>
							</PortalTooltip>
						))}
					</ToggleButtonGroup>
				))}
			</Box>
		</Box>
	);
}

// ── Standalone lasso button (3-state: off → active → has selection) ─────────

// eslint-disable-next-line no-unused-vars
function StandaloneLassoButton({ vm }: { vm: JobMapViewModel }) {
	const hasSelection = vm.effectiveSelectedFeatureIds.size > 0;

	const tooltip = hasSelection
		? "Clear selection"
		: vm.effectiveLassoMode
			? "Click to add points, click first point to close"
			: "Select features";

	return (
		<PortalTooltip title={tooltip}>
			<IconButton
				onClick={vm.onToggleLasso}
				aria-label="select features"
				size="small"
				sx={{
					color: hasSelection ? "error.main" : vm.effectiveLassoMode ? "#2196f3" : undefined,
				}}
			>
				<HighlightAlt />
			</IconButton>
		</PortalTooltip>
	);
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function getSelectionText(selectedIds: Set<string>): string | null {
	if (selectedIds.size === 0) return null;
	let polygons = 0;
	let points = 0;
	selectedIds.forEach((id) => {
		if (id.includes("point")) points++;
		else polygons++;
	});
	const parts: string[] = [];
	if (polygons > 0) parts.push(`${polygons} polygon${polygons !== 1 ? "s" : ""}`);
	if (points > 0) parts.push(`${points} point${points !== 1 ? "s" : ""}`);
	return `Selected: ${parts.join(" and ")}`;
}
